'use client'

import { useCallback } from 'react'
import { useTranslation } from '@/contexts/LanguageContext'

/**
 * Hook for dashboard-specific translations with shorthand helpers.
 *
 * Usage:
 * ```tsx
 * const { t, td, tc, locale, isRTL } = useDashboardTranslation()
 *
 * // Full key path
 * t('dashboard.housekeeping.title')
 *
 * // Shorthand for dashboard keys
 * td('housekeeping.title')
 *
 * // Shorthand for common keys
 * tc('save')  // -> dashboard.common.save
 * tc('status.active')  // -> dashboard.common.status.active
 * ```
 */
export function useDashboardTranslation() {
  const { t, tArray, locale, dir, isRTL } = useTranslation()

  /**
   * Translate a dashboard key with automatic prefix.
   *
   * Wrapped in `useCallback` so the reference is stable across renders. Many
   * components include `td` in `useEffect`/`useCallback`/`useMemo` dep arrays;
   * an unstable reference here causes "Maximum update depth exceeded" loops.
   *
   * @param key - Key path within dashboard namespace (e.g., 'housekeeping.title')
   * @param params - Optional interpolation parameters
   */
  const td = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      return t(`dashboard.${key}`, params)
    },
    [t]
  )

  /**
   * Translate a common dashboard key with automatic prefix.
   *
   * Wrapped in `useCallback` for the same reason as `td` above.
   *
   * @param key - Key path within dashboard.common namespace (e.g., 'save', 'status.active')
   * @param params - Optional interpolation parameters
   */
  const tc = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      return t(`dashboard.common.${key}`, params)
    },
    [t]
  )

  return {
    /** Original translation function (full key path) */
    t,
    /** Dashboard translation helper (prefixes with 'dashboard.') */
    td,
    /** Common translation helper (prefixes with 'dashboard.common.') */
    tc,
    /** Array translation function */
    tArray,
    /** Current locale ('en' | 'ar') */
    locale,
    /** Text direction ('ltr' | 'rtl') */
    dir,
    /** Whether current locale is RTL */
    isRTL,
  }
}
